import type { Object3D } from 'three/webgpu';
import type { EntityPatch } from '../../history/types';
import { createExistingHistorySnapshot } from '../../history/types';
import {
  createAssemblyComponentHistoryPatch,
  type AssemblyComponentHistorySnapshot,
} from '../../history/domain';
import {
  applyComponentInstanceSnapshotToObject,
  createAssemblyTransformHistoryRecord,
  createComponentInstanceSnapshotFromObject,
} from '../component-instance';
import type {
  AssemblyTransformHistoryOperation,
  AssemblyTransformHistoryRecord,
  ComponentInstanceSnapshot,
} from '../component-instance';
import { AssemblyComponentFixationState } from '../component-fixation';

/**
 * 装配体 transform 会话。
 *
 * 设计说明：
 * pointermove / 拖拽预览不进入模型历史，只有 mouseup / OK 的 commit 才会把 before/after 写成 record。
 */
export interface AssemblyTransformHistorySession {
  /** 被记录的目标对象。 */
  readonly target: Object3D;
  /** 操作类型。 */
  readonly operation: AssemblyTransformHistoryOperation;
  /** 变更前组件快照。 */
  readonly before: ComponentInstanceSnapshot;
}

/**
 * 装配体 transform 会话创建输入。
 */
export interface BeginAssemblyTransformHistorySessionInput {
  /** 被记录的目标对象。 */
  readonly target: Object3D;
  /** 操作类型。 */
  readonly operation: AssemblyTransformHistoryOperation;
  /** 可选装配体 ID。 */
  readonly assemblyId?: string;
  /** 可选组件实例 ID。 */
  readonly componentInstanceId?: string;
}

/**
 * 创建装配体 transform 历史会话。
 *
 * @param input 会话创建输入。
 * @returns 历史会话。
 */
export function beginAssemblyTransformHistorySession(
  input: BeginAssemblyTransformHistorySessionInput,
): AssemblyTransformHistorySession {
  return {
    target: input.target,
    operation: input.operation,
    before: createComponentInstanceSnapshotFromObject({
      object: input.target,
      assemblyId: input.assemblyId,
      componentInstanceId: input.componentInstanceId,
    }),
  };
}

/**
 * 提交装配体 transform 历史会话。
 *
 * @param session 历史会话。
 * @returns 发生语义变化时返回历史记录，否则返回 null。
 */
export function commitAssemblyTransformHistorySession(
  session: AssemblyTransformHistorySession,
): AssemblyTransformHistoryRecord | null {
  const after = createComponentInstanceSnapshotFromObject({
    object: session.target,
    assemblyId: session.before.assemblyId,
    componentInstanceId: session.before.componentInstanceId,
  });

  return createAssemblyTransformHistoryRecord(session.operation, session.before, after);
}

/**
 * 丢弃装配体 transform 历史会话并恢复 before 状态。
 *
 * @param session 历史会话。
 */
export function discardAssemblyTransformHistorySession(
  session: AssemblyTransformHistorySession,
): void {
  applyComponentInstanceSnapshotToObject(session.target, session.before);
}

/**
 * 克隆组件 transform 为 history adapter 使用的纯数组结构。
 *
 * @param transform 组件实例 transform 快照。
 * @returns 可进入 ChangeSet 的 transform 快照。
 */
function cloneComponentTransformForHistory(
  transform: ComponentInstanceSnapshot['transform'],
): AssemblyComponentHistorySnapshot['transform'] {
  return {
    position: [
      transform.position[0],
      transform.position[1],
      transform.position[2],
    ],
    quaternion: [
      transform.quaternion[0],
      transform.quaternion[1],
      transform.quaternion[2],
      transform.quaternion[3],
    ],
    scale: [
      transform.scale[0],
      transform.scale[1],
      transform.scale[2],
    ],
  };
}

/**
 * 将组件实例快照转换为 history adapter 快照。
 *
 * @param snapshot 组件实例快照。
 * @returns 装配体 history adapter 快照。
 */
export function convertComponentInstanceSnapshotToAssemblyHistorySnapshot(
  snapshot: ComponentInstanceSnapshot,
): AssemblyComponentHistorySnapshot {
  const fixationState = snapshot.fixationState === AssemblyComponentFixationState.Fixed
    ? 'fixed'
    : 'floating';

  return {
    componentInstanceId: snapshot.componentInstanceId,
    assemblyId: snapshot.assemblyId,
    // history snapshot 会被严格持久化；缺失的可选字段必须省略，不能以 undefined 入库。
    ...(snapshot.sourceNodeId === undefined ? {} : { sourceNodeId: snapshot.sourceNodeId }),
    ...(snapshot.sourcePartFileUuid === undefined ? {} : { sourcePartFileUuid: snapshot.sourcePartFileUuid }),
    ...(snapshot.parentComponentInstanceId === undefined
      ? {}
      : { parentComponentInstanceId: snapshot.parentComponentInstanceId }),
    transform: cloneComponentTransformForHistory(snapshot.transform),
    fixationState,
    ...(snapshot.suppressed === undefined ? {} : { suppressed: snapshot.suppressed }),
  };
}

/**
 * 将装配体 transform 历史记录转换为 EntityPatch。
 *
 * @param record 装配体 transform 历史记录。
 * @returns 装配体组件实体补丁。
 */
export function createAssemblyTransformHistoryPatch(
  record: AssemblyTransformHistoryRecord,
): EntityPatch<AssemblyComponentHistorySnapshot> {
  return createAssemblyComponentHistoryPatch({
    entityId: record.after.componentInstanceId,
    operation: 'update',
    before: createExistingHistorySnapshot(
      convertComponentInstanceSnapshotToAssemblyHistorySnapshot(record.before),
    ),
    after: createExistingHistorySnapshot(
      convertComponentInstanceSnapshotToAssemblyHistorySnapshot(record.after),
    ),
    metadata: {
      assemblyId: record.after.assemblyId,
      componentInstanceId: record.after.componentInstanceId,
    },
  });
}
